import { ArrowRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import type {
  ReportMeasure,
  ReportRow,
  ReportTotals,
} from '@/features/reports/api/use-sales-report-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ShareBar } from '@/features/reports/components/share-bar'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import {
  DAYPART_PRESETS,
  NIGHT,
  daypartRangeLabel,
} from '@/features/reports/lib/dayparts'

interface DaypartBarsCardProps {
  rows: Array<ReportRow>
  total: ReportTotals
  measure: ReportMeasure
  detailsTo: string
  /** Router search params, so a tile hands its own slice to the page it drills into. */
  detailsSearch?: Record<string, unknown>
  loading?: boolean
}

/**
 * The mobile stand-in for the weekday × hour heatmap
 * (CHG-2026-09-manager-reporting).
 *
 * A 7 × 15 grid of cells is unusable at 390px, so the same question — when do we earn — is answered
 * with five bars instead. It is not a second data path: the endpoint's `daypart` dimension does the
 * bucketing, so this is one enum value away from the heatmap and the two cannot disagree.
 */
export function DaypartBarsCard({
  rows,
  total,
  measure,
  detailsTo,
  detailsSearch,
  loading,
}: DaypartBarsCardProps) {
  const { t } = useTranslation('reports')
  const value = (row: ReportRow | undefined) =>
    row == null ? 0 : measure === 'units' ? row.units : row.revenueCents
  const totalValue = measure === 'units' ? total.units : total.revenueCents
  const format = (n: number) =>
    measure === 'units' ? formatNumber(n) : formatCurrency(n)

  const nightRow = rows.find((r) => r.key === NIGHT.key)
  // Night is only shown when something actually happened in it; on a lunch operation it is
  // normally empty, and an always-present zero bar is noise.
  const parts = [...DAYPART_PRESETS, ...(value(nightRow) > 0 ? [NIGHT] : [])]

  const peak = parts.reduce<{ key: string; value: number }>(
    (best, part) => {
      const v = value(rows.find((r) => r.key === part.key))
      return v > best.value ? { key: part.key, value: v } : best
    },
    { key: '', value: 0 },
  )

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          {t('dayparts.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t('dayparts.subtitle')}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <ul className="flex flex-col gap-3">
            {parts.map((part) => {
              const row = rows.find((r) => r.key === part.key)
              return (
                <li
                  key={part.key}
                  className="grid grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)_auto] items-center gap-3"
                >
                  <span className="min-w-0 truncate text-sm">
                    <span className="tabular-nums text-muted-foreground">
                      {daypartRangeLabel(part)}
                    </span>{' '}
                    {t(`dayparts.${part.key}`)}
                  </span>
                  <ShareBar
                    share={totalValue > 0 ? value(row) / totalValue : 0}
                  />
                  <span className="text-right text-sm font-semibold tabular-nums">
                    {format(value(row))}
                  </span>
                </li>
              )
            })}
          </ul>
        )}

        {peak.value > 0 && totalValue > 0 ? (
          <p className="text-xs leading-5 text-muted-foreground">
            {t('dayparts.peakHint', {
              part: t(`dayparts.${peak.key}`),
              share: sharePercent(peak.value / totalValue),
            })}
          </p>
        ) : null}

        <Link
          to={detailsTo}
          search={detailsSearch}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {t('details')}
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </CardContent>
    </Card>
  )
}

const shareFormatter = new Intl.NumberFormat('de-AT', {
  style: 'percent',
  maximumFractionDigits: 0,
})

function sharePercent(share: number): string {
  return shareFormatter.format(share)
}
